import { CheckCircle, CircleAlert, Info, TriangleAlert, X } from "lucide-react"
import { HTMLAttributes, ReactNode, useEffect, useMemo } from "react"
import { getUserMessage, isApiError } from "shared-ts/api/apiError"
import { useUserVisibleErrorLogger } from "shared-ts/hooks/useUserVisibleErrorLogger"

import { classNames } from "../../../../lib/classNames"
import { DooeyIcon } from "../../../DooeyIcon"
import { Icon, IconProps, IconSizes } from "../../../Icon"
import {
  Button,
  ButtonMode,
  ButtonSize,
  ButtonTone,
  ToneProps,
} from "../../control/Button"

export const BannerSeverity = {
  Error: "error",
  Warning: "warning",
  Success: "success",
  Info: "info",
  AI: "ai",
  Neutral: "neutral",
} as const
export type BannerSeverity =
  (typeof BannerSeverity)[keyof typeof BannerSeverity]
// Neutral has no icon, so it has no inline form.
type IconSeverity = Exclude<BannerSeverity, typeof BannerSeverity.Neutral>

export const BannerVariant = {
  Default: "default",
  Inline: "inline",
} as const
export type BannerVariant = (typeof BannerVariant)[keyof typeof BannerVariant]

export const BannerSize = {
  Default: "default",
  Small: "small",
} as const
export type BannerSize = (typeof BannerSize)[keyof typeof BannerSize]

// Paired: a label without a handler (or a handler without a label) fails to
// compile rather than silently rendering no button.
export type BannerActionProps =
  | {
      // Renders a tone-matched tonal action button.
      actionLabel: string
      onAction: () => void
    }
  | { actionLabel?: never; onAction?: never }

type BannerVariantProps =
  | ({
      variant?: typeof BannerVariant.Default
      severity: BannerSeverity
      // Optional bold heading shown above the message.
      title?: string
      message: ReactNode
      children?: ReactNode
      // Renders a dismiss control when provided.
      onClose?: () => void
      // Logged to the user-visible error monitor when severity is error; an
      // ApiError's userMessage also overrides a string message.
      error?: unknown
      loggerScope?: string
      size?: never
    } & BannerActionProps)
  | {
      // Icon-only indicator that a collapsed surface (e.g. a cart line item)
      // carries a banner. Takes no content, action, or dismiss, and never
      // logs — the indicator is not the error surface.
      variant: typeof BannerVariant.Inline
      severity: IconSeverity
      title?: never
      message?: never
      actionLabel?: never
      children?: never
      onAction?: never
      onClose?: never
      error?: never
      loggerScope?: never
      size?: BannerSize
    }

// HTMLElement rather than a concrete tag: the default variant renders a div
// and the inline variant a span, and otherProps spread onto both.
export type BannerV2Props = Omit<HTMLAttributes<HTMLElement>, "title"> &
  BannerVariantProps

export function BannerV2({
  severity,
  variant = BannerVariant.Default,
  title,
  message,
  actionLabel,
  onAction,
  onClose,
  children,
  error,
  loggerScope,
  size = BannerSize.Default,
  className,
  ...otherProps
}: BannerV2Props) {
  const iconSeverity =
    severity === BannerSeverity.Neutral ? undefined : severity

  /* Attempt to override a default string message with ApiError's userMessage. */
  const overriddenMessage = useMemo(() => {
    if (typeof message !== "string") return undefined

    return (isApiError(error) && getUserMessage(error)) || message
  }, [message, error])

  /* Wrap the Error locally to capture the call site stack trace. */
  const wrappedError = useMemo(() => {
    if (!error) return undefined

    const wError = new Error(
      error instanceof Error ? error.message : String(error),
      { cause: error }
    )
    if (error instanceof Error) wError.name = error.name

    return wError
  }, [error])

  const { error: logUserVisibleError, info: logUserVisibleInfo } =
    useUserVisibleErrorLogger(loggerScope ? `banner.${loggerScope}` : "banner")

  const shouldLog =
    severity === BannerSeverity.Error && variant !== BannerVariant.Inline

  useEffect(() => {
    if (!shouldLog || !wrappedError) return

    logUserVisibleError(
      wrappedError,
      overriddenMessage ?? "Unknown user error message"
    )
  }, [shouldLog, wrappedError, overriddenMessage, logUserVisibleError])

  useEffect(() => {
    if (!shouldLog || wrappedError) return

    logUserVisibleInfo(
      "Error Banner shown without an Error object",
      overriddenMessage ?? "Unknown user error message"
    )
  }, [shouldLog, wrappedError, overriddenMessage, logUserVisibleInfo])

  if (variant === BannerVariant.Inline) {
    return (
      <span
        data-testid={`${severity}-banner-inline`}
        role="img"
        aria-label={`${severity} banner`}
        className={classNames(
          "rounded-surface inline-flex shrink-0 items-center justify-center border",
          inlineContainerSizeClassName[size],
          containerClassName[severity],
          className
        )}
        {...otherProps}
      >
        {iconSeverity && (
          <Icon
            icon={severityIcon[iconSeverity]}
            size={inlineIconSize[size]}
            className={toneTextClassName[iconSeverity]}
          />
        )}
      </span>
    )
  }

  return (
    <div
      data-testid={`${severity}-banner`}
      role={severity === BannerSeverity.Error ? "alert" : "status"}
      className={classNames(
        "max-w-150 rounded-surface border p-2",
        containerClassName[severity],
        className
      )}
      {...otherProps}
    >
      <div className="flex items-center gap-x-3">
        {iconSeverity && (
          <Icon
            icon={severityIcon[iconSeverity]}
            size={IconSizes.Small}
            className={classNames("shrink-0", toneTextClassName[iconSeverity])}
          />
        )}
        <div className="flex-1 whitespace-pre-line">
          {title && (
            <div className="text-title-sm text-v2-foreground-default">
              {title}
            </div>
          )}
          <div className="text-body text-v2-foreground-default">
            {overriddenMessage ?? message}
          </div>
        </div>
        {actionLabel && onAction && (
          <Button
            {...actionButtonProps[severity]}
            size={ButtonSize.Small}
            onClick={onAction}
            className="shrink-0"
          >
            {actionLabel}
          </Button>
        )}
        {onClose && (
          <button
            type="button"
            aria-label="Dismiss"
            onClick={onClose}
            className="text-v2-foreground-default-weak hover:text-v2-foreground-default shrink-0"
          >
            <Icon icon={X} size={IconSizes.Small} />
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

const actionButtonProps: Record<BannerSeverity, ToneProps> = {
  [BannerSeverity.Error]: { mode: ButtonMode.Tonal, tone: ButtonTone.Danger },
  [BannerSeverity.Warning]: { mode: ButtonMode.Tonal, tone: ButtonTone.Alert },
  [BannerSeverity.Success]: {
    mode: ButtonMode.Tonal,
    tone: ButtonTone.Success,
  },
  [BannerSeverity.Info]: {
    mode: ButtonMode.Tonal,
    tone: ButtonTone.Information,
  },
  [BannerSeverity.AI]: { mode: ButtonMode.Tonal, tone: ButtonTone.AI },
  [BannerSeverity.Neutral]: { mode: ButtonMode.Ghost },
}

const inlineContainerSizeClassName: Record<BannerSize, string> = {
  [BannerSize.Default]: "h-6.5 w-7.5",
  [BannerSize.Small]: "size-5.5",
}

const inlineIconSize: Record<BannerSize, NonNullable<IconProps["size"]>> = {
  [BannerSize.Default]: IconSizes.ExtraSmall,
  [BannerSize.Small]: IconSizes.ExtraExtraSmall,
}

const severityIcon: Record<IconSeverity, IconProps["icon"]> = {
  [BannerSeverity.Error]: CircleAlert,
  [BannerSeverity.Warning]: TriangleAlert,
  [BannerSeverity.Success]: CheckCircle,
  [BannerSeverity.Info]: Info,
  [BannerSeverity.AI]: DooeyIcon,
}

const containerClassName: Record<BannerSeverity, string> = {
  [BannerSeverity.Error]:
    "bg-v2-background-danger-weak border-v2-border-danger",
  [BannerSeverity.Warning]:
    "bg-v2-background-alert-weak border-v2-border-alert",
  [BannerSeverity.Success]:
    "bg-v2-background-success-weak border-v2-border-success",
  [BannerSeverity.Info]:
    "bg-v2-background-information-weak border-v2-border-information",
  [BannerSeverity.AI]: "bg-v2-background-ai-weak border-v2-border-ai",
  [BannerSeverity.Neutral]: "bg-v2-background-surface border-v2-border-default",
}

// Tone color for the icon; title/message stay neutral in the default variant.
const toneTextClassName: Record<IconSeverity, string> = {
  [BannerSeverity.Error]: "text-v2-foreground-danger",
  [BannerSeverity.Warning]: "text-v2-foreground-alert",
  [BannerSeverity.Success]: "text-v2-foreground-success",
  [BannerSeverity.Info]: "text-v2-foreground-information",
  [BannerSeverity.AI]: "text-v2-foreground-ai",
}
